/**
 * Form validation utilities
 * NOTE: All message fields return i18n translation keys, not plain strings.
 * Screens must wrap errors with t() before displaying: error={error ? t(error) : ''}
 */
import { REGEX } from '../constants';
import type {
  ValidationResult,
  ValidationErrors,
  FormValidationResult,
} from '../types';

/** Per-criterion breakdown of password strength. */
export interface PasswordCriteria {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialChar: boolean;
}

/** A single-field validator: takes a value and returns a validation result. */
export type FieldValidator = (value: unknown) => ValidationResult;

const validateUsernameOrEmail = (identifier?: string): ValidationResult => {
  const value = identifier?.trim();
  if (!value) {
    return { valid: false, message: 'validation.usernameOrEmailRequired' };
  }
  if (value.includes('@')) {
    return validateEmail(value);
  }
  if (value.length < 3) {
    return { valid: false, message: 'validation.usernameTooShort' };
  }
  return { valid: true, message: '' };
};

const validateEmail = (email?: string): ValidationResult => {
  const value = email?.trim();
  if (!value) {
    return { valid: false, message: 'validation.emailRequired' };
  }
  if (!REGEX.EMAIL.test(value)) {
    return { valid: false, message: 'validation.emailInvalid' };
  }
  return { valid: true, message: '' };
};

const checkPasswordCriteria = (password = ''): PasswordCriteria => {
  return {
    hasMinLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    hasSpecialChar: /[@$!%*?&#_%^&*()+=?/\\.,:;'"<>|~`{}[\]-]/.test(password),
  };
};

const validatePassword = (password?: string): ValidationResult => {
  if (!password) {
    return { valid: false, message: 'validation.passwordRequired' };
  }
  const criteria = checkPasswordCriteria(password);
  const isValid = Object.values(criteria).every(Boolean);
  if (!isValid) {
    return { valid: false, message: 'validation.passwordComplex' };
  }
  return { valid: true, message: '' };
};

const validatePhone = (phone?: string): ValidationResult => {
  if (!phone || phone.trim() === '') {
    return { valid: false, message: 'validation.phoneRequired' };
  }
  if (!REGEX.PHONE.test(phone.trim())) {
    return { valid: false, message: 'validation.phoneInvalid' };
  }
  return { valid: true, message: '' };
};

const validateName = (name?: string): ValidationResult => {
  if (!name || name.trim() === '') {
    return { valid: false, message: 'validation.nameRequired' };
  }
  if (!REGEX.NAME.test(name.trim())) {
    return { valid: false, message: 'validation.nameInvalid' };
  }
  return { valid: true, message: '' };
};

const validateRequired = (value?: unknown): ValidationResult => {
  if (!value || String(value).trim() === '') {
    return { valid: false, message: 'validation.fieldRequired' };
  }
  return { valid: true, message: '' };
};

const validateForm = (
  fields: Record<string, unknown>,
  rules: Record<string, FieldValidator>,
): FormValidationResult => {
  const errors: ValidationErrors = {};
  let isValid = true;

  Object.keys(rules).forEach(field => {
    const result = rules[field](fields[field]);
    if (!result.valid) {
      errors[field] = result.message;
      isValid = false;
    }
  });

  return { isValid, errors };
};

const validateSignUp = (data: Record<string, unknown>): ValidationErrors => {
  const validation: ValidationErrors = {};

  Object.entries(data).forEach(([key, rawValue]) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

    if (typeof rawValue === 'string' && !value) {
      validation[key] = 'validation.fieldRequired';
    } else if (key === 'email' && !REGEX.EMAIL.test(String(value))) {
      validation[key] = 'validation.emailInvalid';
    }
  });

  return validation;
};

const validateUsername = (username?: string): ValidationResult => {
  const trimmed = username?.trim() ?? '';
  if (!trimmed) {
    return { valid: false, message: 'validation.fieldRequired' };
  }
  if (trimmed.length < 8) {
    return { valid: false, message: 'validation.usernameTooShort' };
  }
  if (!REGEX.USERNAME.test(trimmed)) {
    return { valid: false, message: 'validation.usernameInvalid' };
  }
  return { valid: true, message: '' };
};

const validateSetupProfile = (
  data: Record<string, unknown>,
): ValidationErrors => {
  let validation: ValidationErrors = {};
  const optionalFields = ['referralCode'];

  Object.keys(data).forEach(key => {
    const rawValue = data[key];
    const stringValue = typeof rawValue === 'string' ? rawValue : '';

    if (
      typeof rawValue === 'string' &&
      !stringValue.trim() &&
      !optionalFields.includes(key)
    ) {
      validation = { ...validation, [key]: 'validation.fieldRequired' };
    } else if (rawValue && key === 'firstName' && !REGEX.NAME.test(stringValue.trim())) {
      validation = { ...validation, [key]: 'validation.firstNameInvalid' };
    } else if (key === 'username') {
      const result = validateUsername(stringValue);
      if (!result.valid) {
        validation = { ...validation, [key]: result.message };
      }
    } else if (rawValue && key === 'password') {
      const criteria = checkPasswordCriteria(stringValue);
      const isValid = Object.values(criteria).every(Boolean);
      if (!isValid) {
        validation = { ...validation, [key]: 'validation.passwordComplex' };
      }
    }
  });

  return validation;
};

const validateLogin = (data: Record<string, unknown>): ValidationErrors => {
  const validation: ValidationErrors = {};

  Object.entries(data).forEach(([key, rawValue]) => {
    const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;

    if (typeof rawValue === 'string' && !value) {
      validation[key] = 'validation.fieldRequired';
    } else if (
      key === 'email' &&
      !REGEX.EMAIL.test(String(value))
    ) {
      validation[key] = 'validation.emailInvalid';
    }
  });

  return validation;
};

const validatePasswordMatch = (
  password: string,
  confirmPassword?: string,
): ValidationResult => {
  if (!confirmPassword) {
    return { valid: false, message: 'validation.confirmPasswordRequired' };
  }
  if (password !== confirmPassword) {
    return { valid: false, message: 'validation.passwordsDoNotMatch' };
  }
  return { valid: true, message: '' };
};

export {
  validateEmail,
  validateUsernameOrEmail,
  validateUsername,
  validatePassword,
  validatePasswordMatch,
  validatePhone,
  validateName,
  validateRequired,
  validateForm,
  validateSignUp,
  validateSetupProfile,
  validateLogin,
  checkPasswordCriteria,
};
